import { useEffect, useMemo, useState } from "react";
import { PageHeader } from "@/components/Layout";
import { EmptyState } from "@/components/EmptyState";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import {
  Plus,
  Trash2,
  Phone,
  Mail,
  Wrench,
  Search,
  Filter,
  Loader2,
  X,
  Users,
  MapPinned,
  UserCheck,
  MapPin,
  Eye,
  EyeOff,
  Pencil,
} from "lucide-react";
import { toast } from "sonner";

const EMPTY_FORM = {
  name: "",
  email: "",
  password: "",
  phone: "",
  specialty: "General",
  address: "",
  lat: "",
  lng: "",
};

const SPECIALTIES = [
  "General",
  "Climatización",
  "Fontanería",
  "Electricidad",
  "Calderas",
  "Piscinas",
  "Energía solar",
  "Jardinería",
];

const ALL_SPECIALTIES = "todas";

function SummaryCard({ icon: Icon, label, value, color }) {
  return (
    <div className="mantix-card flex items-center gap-4 p-5">
      <div
        className="flex h-11 w-11 items-center justify-center rounded-2xl"
        style={{ backgroundColor: color + "15" }}
      >
        <Icon className="h-5 w-5" style={{ color }} strokeWidth={1.8} />
      </div>

      <div>
        <p className="font-display text-2xl font-bold text-[#1D1D1F]">
          {value}
        </p>
        <p className="text-sm text-[#86868B]">{label}</p>
      </div>
    </div>
  );
}

function parseCoordinate(value) {
  if (value === "" || value == null) {
    return null;
  }

  const parsed = Number.parseFloat(value);
  return Number.isFinite(parsed) ? parsed : null;
}

export default function Technicians() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";

  const [techs, setTechs] = useState([]);
  const [query, setQuery] = useState("");
  const [specialtyFilter, setSpecialtyFilter] = useState(ALL_SPECIALTIES);

  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  const [showPassword, setShowPassword] = useState(false);

  const loadTechnicians = async () => {
    try {
      const response = await api.get("/technicians");
      setTechs(response.data || []);
    } catch (error) {
      toast.error(
        formatApiError(error) || "No se pudieron cargar los técnicos."
      );
    }
  };

  useEffect(() => {
    const load = async () => {
      setLoading(true);
      await loadTechnicians();
      setLoading(false);
    };

    load();
  }, []);

  const resetForm = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowPassword(false);
  };

  const openCreate = () => {
    resetForm();
    setOpen(true);
  };

  const openEdit = (technician) => {
    setEditing(technician);

    setForm({
      name: technician.name || "",
      email: technician.email || "",
      password: "",
      phone: technician.phone || "",
      specialty: technician.specialty || "General",
      address: technician.address || "",
      lat: technician.lat == null ? "" : String(technician.lat),
      lng: technician.lng == null ? "" : String(technician.lng),
    });

    setShowPassword(false);
    setOpen(true);
  };

  const save = async (event) => {
    event.preventDefault();

    if (!form.name.trim()) {
      toast.error("Escribe el nombre del técnico.");
      return;
    }

    if (!form.email.trim()) {
      toast.error("Escribe el email de acceso.");
      return;
    }

    if (!editing && form.password.length < 8) {
      toast.error("La contraseña debe tener al menos 8 caracteres.");
      return;
    }

    if (editing && form.password && form.password.length < 8) {
      toast.error("La nueva contraseña debe tener al menos 8 caracteres.");
      return;
    }

    const latitude = parseCoordinate(form.lat);
    const longitude = parseCoordinate(form.lng);

    if (form.lat !== "" && latitude == null) {
      toast.error("La latitud no es válida.");
      return;
    }

    if (form.lng !== "" && longitude == null) {
      toast.error("La longitud no es válida.");
      return;
    }

    if (latitude != null && (latitude < -90 || latitude > 90)) {
      toast.error("La latitud debe estar entre -90 y 90.");
      return;
    }

    if (longitude != null && (longitude < -180 || longitude > 180)) {
      toast.error("La longitud debe estar entre -180 y 180.");
      return;
    }

    setSaving(true);

    try {
      const payload = {
        name: form.name.trim(),
        email: form.email.trim().toLowerCase(),
        phone: form.phone.trim(),
        specialty: form.specialty,
        address: form.address.trim(),
        lat: latitude,
        lng: longitude,
      };

      if (form.password) {
        payload.password = form.password;
      }

      if (editing) {
        await api.put("/technicians/" + editing.id, payload);
        toast.success("Técnico actualizado correctamente.");
      } else {
        await api.post("/technicians", payload);
        toast.success("Técnico añadido correctamente.");
      }

      setOpen(false);
      resetForm();
      await loadTechnicians();
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setSaving(false);
    }
  };

  const remove = async (technician) => {
    const confirmed = window.confirm(
      '¿Seguro que quieres eliminar a "' + technician.name + '"?'
    );

    if (!confirmed) {
      return;
    }

    setDeletingId(technician.id);

    try {
      await api.delete("/technicians/" + technician.id);

      setTechs((current) =>
        current.filter((item) => item.id !== technician.id)
      );

      toast.success("Técnico eliminado.");
    } catch (error) {
      toast.error(formatApiError(error));
    } finally {
      setDeletingId(null);
    }
  };

  const filteredTechs = useMemo(() => {
    const normalizedQuery = query.trim().toLowerCase();

    return techs.filter((technician) => {
      const matchesQuery =
        !normalizedQuery ||
        [
          technician.name,
          technician.email,
          technician.phone,
          technician.specialty,
          technician.address,
        ]
          .filter(Boolean)
          .join(" ")
          .toLowerCase()
          .includes(normalizedQuery);

      const matchesSpecialty =
        specialtyFilter === ALL_SPECIALTIES ||
        technician.specialty === specialtyFilter;

      return matchesQuery && matchesSpecialty;
    });
  }, [techs, query, specialtyFilter]);

  const withPhone = techs.filter((technician) =>
    Boolean(technician.phone)
  ).length;

  const withLocation = techs.filter(
    (technician) =>
      technician.lat != null && technician.lng != null
  ).length;

  const uniqueSpecialties = new Set(
    techs.map((technician) => technician.specialty).filter(Boolean)
  ).size;

  const filtersActive =
    Boolean(query.trim()) || specialtyFilter !== ALL_SPECIALTIES;

  const clearFilters = () => {
    setQuery("");
    setSpecialtyFilter(ALL_SPECIALTIES);
  };

  return (
    <div className="p-6 sm:p-8 lg:p-12">
      <PageHeader
        title="Técnicos"
        subtitle={techs.length + " técnicos en tu equipo"}
        action={
          isAdmin ? (
            <Dialog
              open={open}
              onOpenChange={(nextOpen) => {
                setOpen(nextOpen);

                if (!nextOpen) {
                  resetForm();
                }
              }}
            >
              <DialogTrigger asChild>
                <button
                  data-testid="new-tech-btn"
                  type="button"
                  onClick={openCreate}
                  className="flex items-center gap-2 rounded-full bg-[#0066CC] px-5 py-2.5 font-medium text-white shadow-[0_4px_12px_rgba(0,102,204,0.3)] transition-all hover:-translate-y-0.5 hover:bg-[#004499]"
                >
                  <Plus className="h-4 w-4" />
                  Añadir técnico
                </button>
              </DialogTrigger>

              <DialogContent className="max-h-[90vh] max-w-2xl overflow-y-auto rounded-3xl border-none bg-white p-6 shadow-2xl">
                <DialogHeader>
                  <DialogTitle className="font-display text-2xl font-bold text-[#1D1D1F]">
                    {editing ? "Editar técnico" : "Nuevo técnico"}
                  </DialogTitle>

                  <p className="mt-1 text-sm text-[#86868B]">
                    {editing
                      ? "Modifica sus datos, especialidad y ubicación."
                      : "Crea su acceso y añade los datos necesarios para asignarle trabajos."}
                  </p>
                </DialogHeader>

                <form onSubmit={save} className="mt-4 space-y-5">
                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                      Nombre
                    </label>
                    <input
                      data-testid="tech-name"
                      value={form.name}
                      onChange={(event) =>
                        setForm({ ...form, name: event.target.value })
                      }
                      required
                      autoFocus
                      placeholder="Ej. Carlos Pérez"
                      className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 outline-none focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                        Email de acceso
                      </label>
                      <input
                        data-testid="tech-email"
                        type="email"
                        value={form.email}
                        onChange={(event) =>
                          setForm({ ...form, email: event.target.value })
                        }
                        required
                        placeholder="tecnico@empresa.com"
                        className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 outline-none focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                        {editing ? "Nueva contraseña" : "Contraseña inicial"}
                      </label>

                      <div className="relative">
                        <input
                          data-testid="tech-password"
                          type={showPassword ? "text" : "password"}
                          value={form.password}
                          onChange={(event) =>
                            setForm({ ...form, password: event.target.value })
                          }
                          required={!editing}
                          minLength={form.password ? 8 : undefined}
                          placeholder={
                            editing
                              ? "Déjala vacía para no cambiarla"
                              : "Mínimo 8 caracteres"
                          }
                          className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 pr-11 outline-none focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                        />

                        <button
                          type="button"
                          onClick={() =>
                            setShowPassword((current) => !current)
                          }
                          className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-[#86868B] hover:bg-white"
                          aria-label={
                            showPassword
                              ? "Ocultar contraseña"
                              : "Mostrar contraseña"
                          }
                        >
                          {showPassword ? (
                            <EyeOff className="h-4 w-4" />
                          ) : (
                            <Eye className="h-4 w-4" />
                          )}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                        Teléfono
                      </label>
                      <input
                        data-testid="tech-phone"
                        type="tel"
                        value={form.phone}
                        onChange={(event) =>
                          setForm({ ...form, phone: event.target.value })
                        }
                        placeholder="+34 600 000 000"
                        className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 outline-none focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                        Especialidad
                      </label>
                      <select
                        data-testid="tech-specialty"
                        value={form.specialty}
                        onChange={(event) =>
                          setForm({ ...form, specialty: event.target.value })
                        }
                        className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 outline-none focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                      >
                        {SPECIALTIES.map((specialty) => (
                          <option key={specialty} value={specialty}>
                            {specialty}
                          </option>
                        ))}
                      </select>
                    </div>
                  </div>

                  <div>
                    <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                      Dirección base
                    </label>
                    <input
                      data-testid="tech-address"
                      value={form.address}
                      onChange={(event) =>
                        setForm({ ...form, address: event.target.value })
                      }
                      placeholder="Calle, número, ciudad y provincia"
                      className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 outline-none focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                    />
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                        Latitud
                      </label>
                      <input
                        data-testid="tech-lat"
                        type="number"
                        step="any"
                        value={form.lat}
                        onChange={(event) =>
                          setForm({ ...form, lat: event.target.value })
                        }
                        placeholder="Opcional"
                        className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 outline-none focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                      />
                    </div>

                    <div>
                      <label className="mb-1.5 block text-sm font-medium text-[#1D1D1F]">
                        Longitud
                      </label>
                      <input
                        data-testid="tech-lng"
                        type="number"
                        step="any"
                        value={form.lng}
                        onChange={(event) =>
                          setForm({ ...form, lng: event.target.value })
                        }
                        placeholder="Opcional"
                        className="w-full rounded-xl border border-transparent bg-[#F5F5F7] px-4 py-3 outline-none focus:border-[#0066CC] focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
                      />
                    </div>
                  </div>

                  <p className="rounded-xl bg-[#F5F5F7] px-4 py-3 text-xs leading-relaxed text-[#86868B]">
                    Si dejas las coordenadas vacías y escribes una dirección,
                    Navirox intentará geolocalizarla automáticamente.
                  </p>

                  <div className="flex gap-3 pt-2">
                    <button
                      type="button"
                      onClick={() => setOpen(false)}
                      className="flex-1 rounded-full border border-[#D2D2D7] bg-white py-3 font-medium text-[#1D1D1F] hover:bg-[#F5F5F7]"
                    >
                      Cancelar
                    </button>

                    <button
                      data-testid="save-tech"
                      type="submit"
                      disabled={saving}
                      className="flex flex-1 items-center justify-center gap-2 rounded-full bg-[#0066CC] py-3 font-medium text-white hover:bg-[#004499] disabled:opacity-60"
                    >
                      {saving && (
                        <Loader2 className="h-4 w-4 animate-spin" />
                      )}
                      {saving
                        ? "Guardando..."
                        : editing
                          ? "Guardar cambios"
                          : "Añadir técnico"}
                    </button>
                  </div>
                </form>
              </DialogContent>
            </Dialog>
          ) : null
        }
      />

      <section className="mb-6 grid grid-cols-1 gap-4 sm:grid-cols-3">
        <SummaryCard
          icon={Users}
          label="Técnicos en el equipo"
          value={techs.length}
          color="#0066CC"
        />
        <SummaryCard
          icon={MapPinned}
          label="Con ubicación"
          value={withLocation}
          color="#34C759"
        />
        <SummaryCard
          icon={UserCheck}
          label="Especialidades"
          value={uniqueSpecialties}
          color="#5856D6"
        />
      </section>

      <section className="mantix-card mb-6 p-4">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#86868B]" />
            <input
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder="Buscar por nombre, email, teléfono, especialidad o dirección..."
              className="w-full rounded-xl bg-[#F5F5F7] py-3 pl-11 pr-10 text-sm outline-none focus:bg-white focus:ring-2 focus:ring-[#0066CC]/20"
            />

            {query && (
              <button
                type="button"
                onClick={() => setQuery("")}
                className="absolute right-3 top-1/2 -translate-y-1/2 rounded-lg p-1 text-[#86868B] hover:bg-white"
                aria-label="Borrar búsqueda"
              >
                <X className="h-4 w-4" />
              </button>
            )}
          </div>

          <div className="relative">
            <Filter className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-[#86868B]" />
            <select
              value={specialtyFilter}
              onChange={(event) => setSpecialtyFilter(event.target.value)}
              className="w-full min-w-52 appearance-none rounded-xl bg-[#F5F5F7] py-3 pl-10 pr-8 text-sm outline-none focus:ring-2 focus:ring-[#0066CC]/20 lg:w-auto"
            >
              <option value={ALL_SPECIALTIES}>
                Todas las especialidades
              </option>

              {SPECIALTIES.map((specialty) => (
                <option key={specialty} value={specialty}>
                  {specialty}
                </option>
              ))}
            </select>
          </div>

          {filtersActive && (
            <button
              type="button"
              onClick={clearFilters}
              className="rounded-xl px-4 py-3 text-sm font-medium text-[#0066CC] hover:bg-[#0066CC]/10"
            >
              Limpiar filtros
            </button>
          )}
        </div>

        <p className="mt-3 text-xs text-[#86868B]">
          Mostrando {filteredTechs.length} de {techs.length} técnicos ·{" "}
          {withPhone} con teléfono
        </p>
      </section>

      {loading ? (
        <div className="flex min-h-72 items-center justify-center">
          <div className="text-center">
            <Loader2 className="mx-auto h-7 w-7 animate-spin text-[#0066CC]" />
            <p className="mt-3 text-sm text-[#86868B]">
              Cargando técnicos...
            </p>
          </div>
        </div>
      ) : techs.length === 0 ? (
        <div className="mantix-card py-8">
          <EmptyState
            title="No hay técnicos"
            subtitle="Añade técnicos para asignarles avisos y órdenes de trabajo."
          />

          {isAdmin && (
            <div className="-mt-4 flex justify-center pb-6">
              <button
                type="button"
                onClick={openCreate}
                className="flex items-center gap-2 rounded-full bg-[#0066CC] px-5 py-2.5 text-sm font-medium text-white hover:bg-[#004499]"
              >
                <Plus className="h-4 w-4" />
                Añadir primer técnico
              </button>
            </div>
          )}
        </div>
      ) : filteredTechs.length === 0 ? (
        <div className="mantix-card flex min-h-72 flex-col items-center justify-center p-8 text-center">
          <Search className="h-9 w-9 text-[#B0B0B5]" />
          <h3 className="mt-4 font-display text-lg font-semibold text-[#1D1D1F]">
            No hay resultados
          </h3>
          <p className="mt-1 text-sm text-[#86868B]">
            Prueba a cambiar la búsqueda o la especialidad.
          </p>
          <button
            type="button"
            onClick={clearFilters}
            className="mt-5 rounded-full bg-[#0066CC]/10 px-5 py-2.5 text-sm font-medium text-[#0066CC]"
          >
            Limpiar filtros
          </button>
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {filteredTechs.map((technician) => {
            const isDeleting = deletingId === technician.id;

            return (
              <article
                key={technician.id}
                data-testid={"tech-card-" + technician.id}
                className="mantix-card group p-5 transition-all hover:-translate-y-0.5 hover:shadow-md"
              >
                <div className="flex items-start justify-between gap-3">
                  <button
                    type="button"
                    onClick={() => isAdmin && openEdit(technician)}
                    className="flex min-w-0 flex-1 items-center gap-3 text-left"
                  >
                    <div className="flex h-12 w-12 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-[#0066CC] to-[#5856D6] font-semibold text-white">
                      {technician.name
                        ? technician.name.charAt(0).toUpperCase()
                        : "T"}
                    </div>

                    <div className="min-w-0">
                      <h3 className="truncate font-display text-base font-semibold text-[#1D1D1F]">
                        {technician.name}
                      </h3>
                      <span className="mt-1 inline-flex items-center gap-1 text-xs text-[#86868B]">
                        <Wrench className="h-3 w-3" />
                        {technician.specialty || "General"}
                      </span>
                    </div>
                  </button>

                  {isAdmin && (
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        onClick={() => openEdit(technician)}
                        className="flex h-9 w-9 items-center justify-center rounded-lg text-[#86868B] hover:bg-[#EAF2FC] hover:text-[#0066CC]"
                        aria-label={"Editar " + technician.name}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>

                      <button
                        data-testid={"delete-tech-" + technician.id}
                        type="button"
                        onClick={() => remove(technician)}
                        disabled={isDeleting}
                        className="flex h-9 w-9 items-center justify-center rounded-lg text-[#B0B0B5] hover:bg-[#FFEBEA] hover:text-[#FF3B30] disabled:opacity-50"
                        aria-label={"Eliminar " + technician.name}
                      >
                        {isDeleting ? (
                          <Loader2 className="h-4 w-4 animate-spin" />
                        ) : (
                          <Trash2 className="h-4 w-4" />
                        )}
                      </button>
                    </div>
                  )}
                </div>

                <div className="mt-5 space-y-2.5 text-sm text-[#86868B]">
                  {technician.email && (
                    <a
                      href={"mailto:" + technician.email}
                      className="flex items-center gap-2 break-all hover:text-[#0066CC]"
                    >
                      <Mail className="h-3.5 w-3.5 shrink-0" />
                      {technician.email}
                    </a>
                  )}

                  {technician.phone && (
                    <a
                      href={"tel:" + technician.phone}
                      className="flex items-center gap-2 hover:text-[#0066CC]"
                    >
                      <Phone className="h-3.5 w-3.5 shrink-0" />
                      {technician.phone}
                    </a>
                  )}

                  {technician.address && (
                    <p className="flex items-start gap-2">
                      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span className="line-clamp-2">
                        {technician.address}
                      </span>
                    </p>
                  )}
                </div>

                <div className="mt-5 flex items-center justify-between border-t border-[#E8E8ED] pt-4 text-xs text-[#86868B]">
                  <span>
                    {technician.lat != null && technician.lng != null
                      ? "Ubicación disponible"
                      : "Sin coordenadas"}
                  </span>

                  <span className="rounded-full bg-[#34C759]/10 px-3 py-1 font-medium text-[#248A3D]">
                    Activo
                  </span>
                </div>
              </article>
            );
          })}
        </div>
      )}
    </div>
  );
}